import { useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, CheckCircle2, AlertTriangle, Play, Flag } from "lucide-react";

// Compute hours_before_start client-side (display only).
function hoursBeforeStart(b) {
  try {
    const [y, m, d] = (b.date || "").split("-").map(Number);
    const [hh, mm] = (b.start_time || "").split(":").map(Number);
    // Treat date+time as IST then convert to UTC ms by subtracting 5h30m
    const istMs = Date.UTC(y, m - 1, d, hh, mm) - 5.5 * 60 * 60 * 1000;
    return (istMs - Date.now()) / (1000 * 60 * 60);
  } catch {
    return 0;
  }
}

function bandLabel(h) {
  if (h >= 24) return "≥ 24 hours";
  if (h >= 6) return "6 – 24 hours";
  return "< 6 hours";
}

function policyPreview(role, h, fees) {
  fees = Number(fees || 0);
  if (role === "teacher") {
    if (h >= 24) return { teacher: "No penalty. Cancellation recorded.", parent: `Full refund of ₹${fees}.` };
    if (h >= 6)  return { teacher: "Ranking score -5. Warning recorded.", parent: `Full refund of ₹${fees}.` };
    return { teacher: "Charged ₹200 + ranking score -10.", parent: `Full refund of ₹${fees} + ₹200 bonus.` };
  }
  // parent
  const fullPayout = Math.round(fees * 0.80);
  const halfPayout = Math.round(fees * 0.5 * 0.80);
  if (h >= 24) return { teacher: `Mentor is paid ₹${fullPayout} (after 20% commission).`, parent: `Full refund of ₹${fees}.` };
  if (h >= 6)  return { teacher: `Mentor is paid ₹${halfPayout} (50% after 20% commission).`, parent: `50% refund of ₹${Math.round(fees / 2)}.` };
  return { teacher: `Mentor is paid ₹${fullPayout} (full fees after 20% commission).`, parent: "No refund." };
}

export function CancelButton({ booking, role, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const hours = useMemo(() => hoursBeforeStart(booking), [booking]);
  const preview = useMemo(() => policyPreview(role, hours, booking.fees_snapshot || 0), [role, hours, booking.fees_snapshot]);
  const isPending = booking.status === "pending_teacher_approval";

  const confirm = async () => {
    setSubmitting(true);
    try {
      await api.post(`/bookings/${booking.id}/cancel`, { reason });
      toast.success("Session cancelled");
      setOpen(false);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs px-3 py-1.5 rounded-full border border-red-600 text-red-600 hover:bg-red-50"
        data-testid={testId || `cancel-session-${booking.id}`}
      >
        Cancel
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.45)" }}
          data-testid={`cancel-modal-${booking.id}`}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="bg-white rounded-3xl p-6 max-w-md w-full border border-black/5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-lg font-bold uppercase flex items-center gap-2" style={{ color: "#DC2626" }}>
                  <AlertTriangle className="h-5 w-5" /> Cancel Session?
                </h3>
                <p className="text-xs mt-1" style={{ color: "#13A69999" }}>
                  {booking.date} · {booking.start_time}–{booking.end_time}
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#13A699]" data-testid={`cancel-modal-close-${booking.id}`}>
                <X className="h-5 w-5" />
              </button>
            </div>

            {!isPending && (
              <div className="mt-4 rounded-2xl p-3 text-xs" style={{ background: "#FFF7ED" }}>
                <div className="font-bold uppercase" style={{ color: "#13A699" }}>
                  Time to session: {bandLabel(hours)} ({hours.toFixed(1)}h)
                </div>
                <div className="mt-2" style={{ color: "#13A699" }}>
                  <div><b>Mentor:</b> {preview.teacher}</div>
                  <div className="mt-1"><b>Explorer:</b> {preview.parent}</div>
                </div>
              </div>
            )}
            {isPending && (
              <p className="mt-4 text-xs" style={{ color: "#13A69999" }}>
                This booking has not been approved yet — cancelling is free for both sides.
              </p>
            )}

            <label className="block mt-4 text-xs font-bold uppercase" style={{ color: "#13A699" }}>
              Reason (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
              placeholder="e.g. unexpected schedule conflict"
              data-testid={`cancel-reason-${booking.id}`}
            />

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-sm font-bold px-4 py-2 rounded-full border border-[#13A699]"
                style={{ color: "#13A699" }}
                data-testid={`cancel-keep-${booking.id}`}
              >
                Keep Session
              </button>
              <button
                type="button"
                onClick={confirm}
                disabled={submitting}
                className="text-sm font-bold px-4 py-2 rounded-full bg-red-600 text-white disabled:opacity-60"
                data-testid={`cancel-confirm-${booking.id}`}
              >
                {submitting ? "Cancelling..." : "Confirm Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function PunchInButton({ booking, role, onChange }) {
  const [submitting, setSubmitting] = useState(false);
  const hours = useMemo(() => hoursBeforeStart(booking), [booking]);
  const started = hours <= 0; // session has started or is in the past
  const myPunch = (booking.punch_ins || {})[role];
  const otherRole = role === "parent" ? "teacher" : "parent";
  const otherPunch = (booking.punch_ins || {})[otherRole];
  const otherLabel = otherRole === "teacher" ? "Mentor" : "Explorer";

  const submit = async () => {
    if (!started) {
      toast.error("Session end opens after the scheduled start time");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/punch-in`);
      if (data.status === "completed") {
        toast.success("Session ended by both sides — payout queued");
      } else {
        toast.success(`You've marked Session Ended. Waiting for ${otherLabel}.`);
      }
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not mark session ended");
    } finally {
      setSubmitting(false);
    }
  };

  if (myPunch) {
    return (
      <span
        className="text-xs font-bold uppercase px-3 py-1.5 rounded-full inline-flex items-center gap-1"
        style={{ background: otherPunch ? "#13A699" : "#FFD708", color: otherPunch ? "#FFF7ED" : "#13A699" }}
        data-testid={`session-ended-${booking.id}`}
      >
        <CheckCircle2 className="h-3 w-3" />
        {otherPunch ? "Session Ended" : `Waiting for ${otherLabel}`}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={submit}
      disabled={!started || submitting}
      title={!started ? "Available after the session start time" : ""}
      className="text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ background: "#FFD708", color: "#13A699" }}
      data-testid={`session-end-${booking.id}`}
    >
      <CheckCircle2 className="h-3 w-3" /> {submitting ? "Submitting..." : "Session Ended"}
    </button>
  );
}


// ---------------------- Start Session ----------------------
export function StartSessionButton({ booking, role, onChange }) {
  const [submitting, setSubmitting] = useState(false);
  const starts = booking.session_starts || {};
  const myStart = starts[role];
  const otherRole = role === "parent" ? "teacher" : "parent";
  const otherStart = starts[otherRole];
  const otherLabel = otherRole === "teacher" ? "Mentor" : "Explorer";

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/start-session`);
      if (data.both_started) {
        toast.success("Both sides have started the session.");
      } else {
        toast.success(`You've marked Session Started. Waiting for ${otherLabel}.`);
      }
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start session");
    } finally {
      setSubmitting(false);
    }
  };

  if (myStart) {
    return (
      <span
        className="text-xs font-bold uppercase px-3 py-1.5 rounded-full inline-flex items-center gap-1"
        style={{ background: otherStart ? "#13A699" : "#FFD70855", color: otherStart ? "#FFF7ED" : "#13A699" }}
        data-testid={`session-started-${booking.id}`}
      >
        <Play className="h-3 w-3" />
        {otherStart ? "Session Started" : `Waiting for ${otherLabel}`}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={submit}
      disabled={submitting}
      className="text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ background: "#13A699", color: "#FFF7ED" }}
      data-testid={`session-start-${booking.id}`}
    >
      <Play className="h-3 w-3" /> {submitting ? "Submitting..." : "Session Started"}
    </button>
  );
}


// ---------------------- Report Session ----------------------
export function ReportSessionButton({ booking, role, onChange }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [category, setCategory] = useState("other");
  const [submitting, setSubmitting] = useState(false);

  const canReport = booking.status === "completed";

  const submit = async () => {
    if (reason.trim().length < 5) return toast.error("Please describe the issue (min 5 chars).");
    setSubmitting(true);
    try {
      await api.post(`/bookings/${booking.id}/report`, { reason: reason.trim(), category });
      toast.success("Report filed. Our team will review it.");
      setOpen(false);
      setReason("");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not file report");
    } finally {
      setSubmitting(false);
    }
  };

  if (!canReport) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs px-3 py-1.5 rounded-full border border-red-600 text-red-600 hover:bg-red-50 inline-flex items-center gap-1"
        data-testid={`session-report-${booking.id}`}
      >
        <Flag className="h-3 w-3" /> Report
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.45)" }}
          data-testid={`report-modal-${booking.id}`}
        >
          <div className="bg-[#FFF7ED] rounded-2xl w-full max-w-md p-5 border border-black/10">
            <div className="flex items-start justify-between">
              <h3 className="font-display text-lg font-bold" style={{ color: "#13A699" }}>
                Report an issue with this session
              </h3>
              <button onClick={() => setOpen(false)} data-testid={`report-close-${booking.id}`}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <label className="block text-xs font-semibold mt-4" style={{ color: "#13A699" }}>Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full mt-1 rounded-xl border border-[#13A69955] px-3 py-2 bg-white text-sm"
              data-testid={`report-category-${booking.id}`}
            >
              <option value="no_show">No-show</option>
              <option value="late">Late arrival</option>
              <option value="content">Content quality</option>
              <option value="behaviour">Unprofessional behaviour</option>
              <option value="safety">Safety concern</option>
              <option value="other">Other</option>
            </select>
            <label className="block text-xs font-semibold mt-3" style={{ color: "#13A699" }}>What happened?</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="Give us the facts so we can help."
              className="w-full mt-1 rounded-xl border border-[#13A69955] px-3 py-2 bg-white text-sm"
              data-testid={`report-reason-${booking.id}`}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-xs px-4 py-2 rounded-full border border-black/10"
                data-testid={`report-cancel-${booking.id}`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="text-xs font-bold px-4 py-2 rounded-full text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                data-testid={`report-submit-${booking.id}`}
              >
                <AlertTriangle className="h-3 w-3 inline-block mr-1" />
                {submitting ? "Submitting..." : "File report"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function DemoCancelButton({ demo, onChange }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const confirm = async () => {
    setSubmitting(true);
    try {
      await api.post(`/demo/${demo.id}/cancel`, { reason });
      toast.success(`Demo class cancelled. Full refund of ₹${demo.amount} will be processed.`);
      setOpen(false);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs px-3 py-1.5 rounded-full border border-red-600 text-red-600 hover:bg-red-50"
        data-testid={`cancel-demo-${demo.id}`}
      >
        Cancel
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.45)" }}
          data-testid={`cancel-demo-modal-${demo.id}`}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="bg-white rounded-3xl p-6 max-w-md w-full border border-black/5">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-display text-lg font-bold uppercase flex items-center gap-2" style={{ color: "#DC2626" }}>
                <AlertTriangle className="h-5 w-5" /> Cancel Demo Class?
              </h3>
              <button onClick={() => setOpen(false)} className="text-[#13A699]" data-testid={`cancel-demo-close-${demo.id}`}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4 rounded-2xl p-3 text-xs" style={{ background: "#FFF7ED", color: "#13A699" }}>
              The parent will receive a <b>full refund of ₹{demo.amount}</b>. The mentor will be notified.
            </div>
            <label className="block mt-4 text-xs font-bold uppercase" style={{ color: "#13A699" }}>
              Reason (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
              data-testid={`cancel-demo-reason-${demo.id}`}
            />
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-sm font-bold px-4 py-2 rounded-full border border-[#13A699]"
                style={{ color: "#13A699" }}
                data-testid={`cancel-demo-keep-${demo.id}`}
              >
                Keep Demo
              </button>
              <button
                type="button"
                onClick={confirm}
                disabled={submitting}
                className="text-sm font-bold px-4 py-2 rounded-full bg-red-600 text-white disabled:opacity-60"
                data-testid={`cancel-demo-confirm-${demo.id}`}
              >
                {submitting ? "Cancelling..." : "Confirm Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

